import React from 'react';
import { Link } from 'react-router-dom';
import { Text } from 'components/Text';
import {
  getUniqueTrackingNumbers,
  TrackingLink,
} from 'components/TrackingLink';
import { getStoreCurrencyFormatter, wasOrderUpdated } from 'utils';
import { withRequestReturnTo } from 'utils/requestNavigation';
import { getRequestTypeLabel } from 'utils/requestTypePresentation';

const RETURN_STATUS_LABELS: Record<string, string> = {
  SUBMITTED: 'Return filed',
  APPROVED: 'Return approved',
  AWAITING_DROPOFF: 'Awaiting customer shipment',
  LABEL_READY: 'Awaiting customer shipment',
  IN_TRANSIT: 'Return in transit',
  RECEIVED: 'Return received',
  DENIED: 'Return denied',
  RESOLVED: 'Return resolved',
  EXPIRED: 'Return expired',
};

type RequestPreview = {
  id: string;
  number?: string | number | null;
  status?: string | null;
  requestedResolution?: string | null;
  returnItems?: Array<{requestedResolution?: string | null}> | null;
};

const OrderRequestsSummary = ({ item }: { item: any }) => {
  const issueCount = item?.claimsAggregate?.aggregate?.count;
  const returnCount = item?.returnsAggregate?.aggregate?.count;
  const countsAvailable =
    Number.isInteger(issueCount) && Number.isInteger(returnCount);
  const returnLocation = item.returnLocation || '/orders';
  const requestListUrl = (type?: string) => {
    const params = new URLSearchParams({ orderId: item.id, group: 'ALL' });
    if (type) params.set('type', type);
    return withRequestReturnTo(`/requests?${params}`, returnLocation);
  };
  const asPreviews = (records: RequestPreview[] | null = [], type: string) =>
    (records ?? []).map((record) => ({ ...record, type,
      typeLabel: getRequestTypeLabel({type: type === 'Issue' ? 'ISSUE' : 'RETURN', requestedResolution: record.requestedResolution, items: record.returnItems}),
    }));
  const openIssues = asPreviews(item.openClaims, 'Issue');
  const openReturns = asPreviews(item.openReturns, 'Return');
  const candidates = [
    ...openIssues.slice(0, 1),
    ...openReturns.slice(0, 1),
    ...openIssues.slice(1),
    ...openReturns.slice(1),
    ...asPreviews(item.claims, 'Issue'),
    ...asPreviews(item.returns, 'Return'),
  ];
  const previews = candidates
    .filter(
      (record, index) =>
        record.id &&
        candidates.findIndex(
          (candidate) =>
            candidate.id === record.id && candidate.type === record.type,
        ) === index,
    )
    .slice(0, 2);

  if (countsAvailable && issueCount === 0 && returnCount === 0)
    return <Text value="None" type="xs" className="text-gray-500" />;

  return (
    <div className="min-w-[12rem] max-w-xs whitespace-normal text-xs">
      {countsAvailable ? (
        <div className="flex flex-wrap gap-x-1">
          <Link
            className="text-blue-700 underline"
            to={requestListUrl('ISSUE')}
          >
            {issueCount} {issueCount === 1 ? 'issue' : 'issues'}
          </Link>
          <span aria-hidden="true">·</span>
          <Link
            className="text-blue-700 underline"
            to={requestListUrl('RETURN')}
          >
            {returnCount} {returnCount === 1 ? 'return / exchange' : 'returns / exchanges'}
          </Link>
        </div>
      ) : (
        <Link className="text-blue-700 underline" to={requestListUrl()}>
          View requests
        </Link>
      )}
      {previews.map((record) => (
        <Link
          key={`${record.type}-${record.id}`}
          className="block mt-1 text-blue-700 underline"
          to={withRequestReturnTo(
            `/${
              record.type === 'Issue' ? 'claims' : 'returns'
            }/${encodeURIComponent(record.id)}`,
            returnLocation,
          )}
        >
          {record.typeLabel} {record.number || record.id}
          {record.status && (
            <span className="text-gray-600">
              {' · '}
              {record.type === 'Return'
                ? RETURN_STATUS_LABELS[record.status] ||
                  record.status.replace(/_/g, ' ')
                : record.status.replace(/_/g, ' ')}
            </span>
          )}
        </Link>
      ))}
      {countsAvailable && (
        <Link
          className="inline-block mt-1 text-blue-700 underline"
          to={requestListUrl()}
        >
          View all
        </Link>
      )}
    </div>
  );
};

export const columns = [
  {
    name: 'Delivery Guarantee Status',
    id: `guarantee-status-${Date.now()}`,
    render: (item: any) => {
      if (item?.protectionStatus?.toLowerCase() === 'protected')
        return (
          <Text
            value="Guaranteed"
            className="bg-green-100 px-3 py-1 rounded-full text-green-700 text-center"
            type="xs"
          />
        );
      if (item?.protectionStatus?.toLowerCase() === 'unprotected')
        return (
          <Text
            value="Not Guaranteed"
            className="bg-red-100 px-3 py-1 rounded-full text-red-700 text-center"
            type="xs"
          />
        );
      return (
        <Text
          value={`${item?.protectionStatus?.replace('Claim', 'Issue')}`}
          className="bg-blue-100 px-3 py-1 rounded-full text-center"
          type="xs"
        />
      );
    },
    value: 'protectionStatus',
  },
  {
    name: 'Requests',
    id: 'order-requests',
    render: (item: any) => <OrderRequestsSummary item={item} />,
  },
  {
    name: 'Tracking Status',
    id: `tracking-status-${Date.now()}`,
    render: (item: any) => {
      const status = shipmentStatus.find(
        (s) => s.value === item.trackingStatus,
      );
      return (
        <Text className={`${status?.textColor}`} value={status?.label ?? ''} />
      );
    },
  },
  {
    name: 'Tracking',
    id: `tracking-${Date.now()}`,
    render: (item: any) => {
      const [trackingNumber] = getUniqueTrackingNumbers(item?.trackings);
      if (trackingNumber && item?.id) {
        return (
          <TrackingLink
            trackingNumber={trackingNumber}
            ownerType="ORDER"
            ownerId={item.id}
          >
            <Text value="Track" type="body" className="underline" />
          </TrackingLink>
        );
      }
      return <Text value={'N/A'} />;
    },
    value: 'tracking',
  },
  {
    name: 'Order Edited',
    id: `order-edited-${Date.now()}`,
    render: (item: any) => {
      return <Text value={wasOrderUpdated(item?.activities) ? 'YES' : 'NO'} />;
    },
  },
  {
    name: 'Tree Planted',
    id: `tree-planted-${Date.now()}`,
    render: (item: any) => {
      return (
        <Text value={item?.treeDetails?.id && !item?.refunded ? 'YES' : 'NO'} />
      );
    },
  },
  {
    name: 'Order Date',
    id: `order-date-${Date.now()}`,
    render: (item: any) => (
      <Text value={new Date(item.createdAt).toLocaleDateString()} />
    ),
    value: 'createdAt',
  },
  {
    name: 'Delivery Guarantee Paid',
    id: `guarantee-paid-${Date.now()}`,
    render: (item: any) => (
      <Text
        value={`${getStoreCurrencyFormatter(
          item.currency,
          item.protectionTotal,
        )}`}
      />
    ),
    value: 'protectionTotal',
  },
  {
    name: 'Customer Name',
    id: `customer-name-${Date.now()}`,
    key: 'customerName',
    value: 'customerName',
  },
  {
    name: 'Customer Email',
    id: `customer-email-${Date.now()}`,
    render: (item: any) => <Text value={`${item.customerEmail ?? 'N/A'}`} />,
    value: 'customerEmail',
  },
  {
    name: 'Items',
    id: `items-${Date.now()}`,
    render: (item: any) => (
      <Text
        value={`${
          item?.items?.filter((item: any) => !item.title?.includes('ShipAid'))
            .length
        } items`}
      />
    ),
    value: 'items',
  },
  {
    name: 'Order Total',
    id: `order-total-${Date.now()}`,
    render: (item: any) => (
      <Text value={`${getStoreCurrencyFormatter(item.currency, item.total)}`} />
    ),
    value: 'total',
  },
];

export const stats = [
  {
    name: 'Total Orders',
    currentStat: '70',
    previousStat: '60',
    change: '16',
    changeType: 'increase',
  },
  {
    name: 'Guaranteed Orders',
    currentStat: '60',
    previousStat: '70',
    change: '16',
    changeType: 'decrease',
  },
  {
    name: 'Reimbursed/Reshipped',
    currentStat: '100',
    previousStat: '50',
    change: '50',
    changeType: 'increase',
  },
];

export const trackingColumns = [
  {
    name: 'Tracking Number',
    id: 101,
    render: (item: any) =>
      item.trackingNumber?.trim() && item.ownerId ? (
        <TrackingLink
          trackingNumber={item.trackingNumber}
          ownerType="ORDER"
          ownerId={item.ownerId}
        />
      ) : (
        <Text value="N/A" />
      ),
    value: 'trackingNumber',
  },
  {
    name: 'Tracking',
    id: 102,
    render: (item: any) =>
      item.trackingNumber?.trim() && item.ownerId ? (
        <TrackingLink
          trackingNumber={item.trackingNumber}
          ownerType="ORDER"
          ownerId={item.ownerId}
        >
          <Text value="Track" type="body" className="underline" />
        </TrackingLink>
      ) : (
        <Text value="N/A" />
      ),
  },
  {
    name: 'Status',
    id: 103,
    render: (item: any) => {
      const status = shipmentStatus.find((s) => s.value === item.trackerStatus);
      return (
        <Text className={`${status?.textColor}`} value={status?.label ?? ''} />
      );
    },
  },
  {
    name: 'Item',
    id: 104,
    render: (item: any) => <Text value={item.orderItem.title} />,
  },
  {
    name: 'Carrier',
    id: 105,
    key: 'trackingCompany',
    value: 'trackingCompany',
  },
  {
    name: 'Est Delivery',
    id: 106,
    render: (item: any) => (
      <Text
        value={
          item.estDeliveryDate
            ? new Date(item.estDeliveryDate).toLocaleDateString()
            : 'N/A'
        }
      />
    ),
  },
];

export const shipmentStatus = [
  {
    label: 'Pre-Transit',
    value: 'PRE_TRANSIT',
    textColor: 'text-indigo-800',
  },
  {
    label: 'In-Transit',
    value: 'IN_TRANSIT',
    textColor: 'text-blue-500',
  },
  {
    label: 'Out For Delivery',
    value: 'OUT_FOR_DELIVERY',
    textColor: 'text-orange-500',
  },
  {
    label: 'Delivered',
    value: 'DELIVERED',
    textColor: 'text-green-500',
  },
  {
    label: 'Avaliable For Pickup',
    value: 'AVAILABLE_FOR_PICKUP',
    textColor: 'text-orange-500',
  },
  {
    label: 'Exception',
    value: 'ERROR',
    textColor: 'text-red-800',
  },
  {
    label: 'Failed Attempt',
    value: 'FAILURE',
    textColor: 'text-indigo-700',
  },
  {
    label: 'Cancelled',
    value: 'CANCELLED',
    textColor: 'text-gray-700',
  },
  {
    label: 'Return To Sender',
    value: 'RETURN_TO_SENDER',
    textColor: 'text-amber-700',
  },
  {
    label: 'Pending',
    value: 'UNKNOWN',
    textColor: 'text-gray-400',
  },
];

export const claimReasons = [
  {
    label: 'LOST',
    value: 'LOST',
    textColor: 'text-indigo-800',
  },
  {
    label: 'STOLEN',
    value: 'STOLEN',
    textColor: 'text-indigo-800',
  },
  {
    label: 'DAMAGED',
    value: 'DAMAGED',
    textColor: 'text-orange-500',
  },
  {
    label: 'LATE',
    value: 'LATE',
    textColor: 'text-blue-500',
  },
  {
    label: 'OTHER',
    value: 'OTHER',
    textColor: 'text-green-500',
  },
];

export const OrderStatus = {
  CANCELLED: {
    label: 'Cancelled',
    value: 'CANCELLED',
    textColor: 'text-red-700',
    bgColor: 'bg-red-100',
  },
  CLAIM_FILED: {
    label: 'Claim Filed',
    value: 'CLAIM_FILED',
    textColor: 'text-red-700',
    bgColor: 'bg-red-100',
  },
  GUARANTEED: {
    label: 'Guaranteed',
    value: 'GUARANTEED',
    textColor: 'text-green-700',
    bgColor: 'bg-green-100',
  },
  NOT_GUARANTEED: {
    label: 'Not Guaranteed',
    value: 'NOT_GUARANTEED',
    textColor: 'text-red-700',
    bgColor: 'bg-red-100',
  },
};
